/**
 * Report periods for the admin's Transactions and Orders pages: presets (this month, last quarter,
 * this financial year, …) as ranges of IST days, a label for one, and the GSTR-1 period a range
 * files under (a calendar month, or a quarter — QRMP files under its last month).
 *
 * Days are "YYYY-MM-DD" in IST, as the API reads them; both ends included; "" leaves an end open.
 * India's financial year runs April to March.
 */

export type PeriodPreset =
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "this-fy"
  | "last-fy"
  | "today"
  | "all"
  | "custom";

export interface DayRange {
  from: string;
  to: string;
}

export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: "this-month", label: "This month" },
  { value: "last-month", label: "Last month" },
  { value: "this-quarter", label: "This quarter" },
  { value: "last-quarter", label: "Last quarter" },
  { value: "this-fy", label: "This financial year" },
  { value: "last-fy", label: "Last financial year" },
  { value: "today", label: "Today" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom dates" },
];

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
/** Days in month `m` (1–12). */
const monthEnd = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Today in IST (UTC+5:30, no daylight saving), whatever the device's zone. */
export function istToday(now: Date = new Date()): { y: number; m: number; d: number } {
  const ist = new Date(now.getTime() + 330 * 60_000);
  return { y: ist.getUTCFullYear(), m: ist.getUTCMonth() + 1, d: ist.getUTCDate() };
}

function quarter(y: number, firstMonth: number): DayRange {
  return { from: ymd(y, firstMonth, 1), to: ymd(y, firstMonth + 2, monthEnd(y, firstMonth + 2)) };
}

/** The days a preset covers on `now`'s IST date; "all" (and "custom") leave both ends open. */
export function presetRange(preset: PeriodPreset, now: Date = new Date()): DayRange {
  const { y, m, d } = istToday(now);
  const fy = m >= 4 ? y : y - 1; // the financial year that started in April of `fy`
  switch (preset) {
    case "today":
      return { from: ymd(y, m, d), to: ymd(y, m, d) };
    case "this-month":
      return { from: ymd(y, m, 1), to: ymd(y, m, monthEnd(y, m)) };
    case "last-month": {
      const [py, pm] = m === 1 ? [y - 1, 12] : [y, m - 1];
      return { from: ymd(py, pm, 1), to: ymd(py, pm, monthEnd(py, pm)) };
    }
    case "this-quarter":
      return quarter(y, m - ((m - 1) % 3));
    case "last-quarter": {
      const first = m - ((m - 1) % 3) - 3;
      return first < 1 ? quarter(y - 1, first + 12) : quarter(y, first);
    }
    case "this-fy":
      return { from: ymd(fy, 4, 1), to: ymd(fy + 1, 3, 31) };
    case "last-fy":
      return { from: ymd(fy - 1, 4, 1), to: ymd(fy, 3, 31) };
    default:
      return { from: "", to: "" };
  }
}

function parse(day: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** GSTR-1's period ("102026", "092026") for a calendar month or quarter; null for any other range. */
export function gstr1Period(range: DayRange): string | null {
  const a = parse(range.from);
  const b = parse(range.to);
  if (!a || !b) return null;
  const [fy, fm, fd] = a;
  const [ty, tm, td] = b;
  if (fd !== 1 || td !== monthEnd(ty, tm)) return null;
  const months = (ty - fy) * 12 + tm - fm + 1;
  return months === 1 || (months === 3 && [1, 4, 7, 10].includes(fm)) ? `${pad(tm)}${ty}` : null;
}

/** "1 Oct 2026" for an IST day. */
export function dayLabel(day: string): string {
  const p = parse(day);
  if (!p) return day;
  return new Date(Date.UTC(p[0], p[1] - 1, p[2])).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "1 Oct 2026 – 31 Oct 2026", "From 1 Oct 2026", "All time". */
export function rangeLabel(range: DayRange): string {
  if (range.from && range.to) return range.from === range.to ? dayLabel(range.from) : `${dayLabel(range.from)} – ${dayLabel(range.to)}`;
  if (range.from) return `From ${dayLabel(range.from)}`;
  if (range.to) return `Up to ${dayLabel(range.to)}`;
  return "All time";
}

/** The preset a range is, on `now`'s date ("custom" when none matches). */
export function presetOf(range: DayRange, now: Date = new Date()): PeriodPreset {
  const match = PERIOD_PRESETS.find(
    (p) => p.value !== "custom" && presetRange(p.value, now).from === range.from && presetRange(p.value, now).to === range.to,
  );
  return match?.value ?? "custom";
}
